/**
 * Wendet Darstellungs-Einstellungen auf das Dokument an: Theme (System/Dunkel/Hell/Eigenes), Schriftgröße,
 * reduzierte Bewegung, Theme-Variablen und eigenes CSS. Nutzer-CSS steht in <style> NACH den Basis-Styles
 * und ist bereinigt (lib/cssSanitizer).
 */
import { useQuery } from '@tanstack/react-query';
import type { UserSettingsDto } from '@lagerfeuer/shared';
import { useEffect, useSyncExternalStore } from 'react';
import { create } from 'zustand';
import { sanitizeCss, sanitizeThemeVariables, themeVariablesCss } from '../../lib/cssSanitizer';
import { usersApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { useAppStore } from '../../store/app';
import { useAuthStore } from '../../store/auth';

/** Vorschau aus den Einstellungen (ungespeichert) – überschreibt die gespeicherten Werte, solange gesetzt. */
export interface ThemePreview {
  theme?: UserSettingsDto['theme'];
  customCss?: string | null;
  variables?: Record<string, string> | null;
  fontScale?: number;
}

export const useThemePreview = create<{ preview: ThemePreview | null; setPreview(p: ThemePreview | null): void }>((set) => ({
  preview: null,
  setPreview: (preview) => set({ preview }),
}));

function subscribeScheme(cb: () => void) {
  const mq = window.matchMedia?.('(prefers-color-scheme: light)');
  mq?.addEventListener?.('change', cb);
  return () => mq?.removeEventListener?.('change', cb);
}
const prefersLight = () => !!window.matchMedia?.('(prefers-color-scheme: light)').matches;

function styleEl(id: string): HTMLStyleElement {
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    // Immer ans Ende von <head>, also nach den Basis-Styles.
    document.head.appendChild(el);
  }
  return el;
}

export function resolveTheme(theme: UserSettingsDto['theme'] | undefined, systemLight: boolean): 'dark' | 'light' {
  if (theme === 'LIGHT') return 'light';
  if (theme === 'DARK') return 'dark';
  return systemLight ? 'light' : 'dark';
}

export function ThemeManager() {
  const settings = useAppStore((s) => s.settings);
  const authed = useAuthStore((s) => !!s.accessToken);
  const preview = useThemePreview((s) => s.preview);
  const systemLight = useSyncExternalStore(subscribeScheme, prefersLight, () => false);
  const activeThemeId = settings?.activeThemeId ?? null;
  const themes = useQuery({ queryKey: qk.themes, queryFn: usersApi.themes, enabled: authed && !!activeThemeId, staleTime: 5 * 60_000 });
  const active = themes.data?.find((t) => t.id === activeThemeId) ?? null;

  const theme = preview?.theme ?? settings?.theme;
  const fontScale = preview?.fontScale ?? settings?.fontScale ?? 1;
  const reducedMotion = settings?.reducedMotion ?? false;
  const customCss = preview && 'customCss' in preview ? preview.customCss : settings?.customCss;
  const variables = preview && 'variables' in preview ? preview.variables : active?.variables;
  const themeCss = active?.css;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolveTheme(theme, systemLight);
    root.style.setProperty('--lf-font-scale', String(Math.min(1.5, Math.max(0.75, fontScale || 1))));
    root.dataset.reducedMotion = String(reducedMotion);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', root.dataset.theme === 'light' ? '#f3ebe2' : '#141110');
  }, [theme, systemLight, fontScale, reducedMotion]);

  useEffect(() => {
    styleEl('lf-theme-vars').textContent = themeVariablesCss(sanitizeThemeVariables(variables ?? null));
    const css = [themeCss, theme === 'CUSTOM' || customCss ? customCss : null].filter(Boolean).join('\n');
    styleEl('lf-user-css').textContent = sanitizeCss(css);
  }, [variables, themeCss, customCss, theme]);

  return null;
}
